import * as React from "react";
import {
    useArchiveParticipantMutation,
    useLazyGetCclParticipantsWithFilesByCoachImKeyQuery,
    useLazyGetParticipantCompassUrlQuery,
    useUnarchiveParticipantMutation,
} from "../../../../../services/cclTokenedEnterpriseParticipantApi";
import FlexGrid from "../../../../../layouts/flexGrid";
import { ResourceRegistrationsListDataGridPro } from "./resourceParticipantList";
import { Alert, AlertColor, AlertTitle, Stack } from "@mui/material";
import ComponentLoader from "../../../../../components/common/componentLoader";
import { getErrorMsg } from "../../../../../utilities/rtkQueryErrorHelpers";
import {
    AssetDownloadRequest,
    IndividualAssetsList,
    IndividualAssetsReleaseRequest,
} from "../../../../../services/types/assetApiTypes";
import { useReleaseAssetsMutation } from "../../../../../services/cclTokenedAssetsApi";
import { UserClaimsService } from "../../../../../services/currentUserService/currentUserService";
import {
    Asset,
    GetCompassUrlResponse,
    Participant,
} from "../../../../../services/types/enterpriseParticipantApiTypes";
import { useSendCompassInvitationsMutation } from "../../../../../services/cclTokenedGrandCentralApi";
import {
    RecipientInfo,
    SendCompassInvitationRequest,
    SendInvitationResponse,
} from "../../../../../services/types/rtkQueryTypes";
import { FileDownloadService } from "../../../../../services/fileDownloadService/fileDownloadService";
import useLogAccessEvent from "../../../../../hooks/useLogAccessEvent";
import CclAlertSnackbar from "../../../../../components/common/cclAlertSnackbar";
import CclDownloadWarningAlert from "../../../../../components/common/cclDownloadWarningAlert";

interface ResourceRegistrationsPanelProps {
    imkey: number;
    alternateEventOnClickHandler?: any;
    handleViewPaxAssessmentStatus: (esiKey: number) => void;
    isUserDetail: boolean;
}

const ResourceRegistrationsPanel: React.FC<ResourceRegistrationsPanelProps> = (props) => {
    const [participants, setParticipants] = React.useState<Participant[]>([]);
    const [currentEvent, setCurrentEvent] = React.useState<string>("");
    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor | undefined>("info");

    const [getParticipantsTrigger, { data: allParticipants, isLoading, isError, error }] =
        useLazyGetCclParticipantsWithFilesByCoachImKeyQuery();
    const [releaseAssets] = useReleaseAssetsMutation();
    const [archiveParticipants] = useArchiveParticipantMutation();
    const [unarchiveParticipants] = useUnarchiveParticipantMutation();
    const { logEvent } = useLogAccessEvent();
    const [sendCompassInvitations] = useSendCompassInvitationsMutation();
    const [getCompassUrl] = useLazyGetParticipantCompassUrlQuery();

    const downloadService = new FileDownloadService();
    const claimsService = new UserClaimsService();
    const allowParticipantOperations: boolean =
        claimsService.IsUserCclAdminOrSuperAdmin() ||
        props.imkey.toString() === claimsService.GetCurrentUserId();
    // super admins can archive/un-archive on behalf of the user being viewed on user detail
    const archiveOnBehalfOfUser: boolean = props.isUserDetail && claimsService.IsUserSuperAdmin();

    React.useEffect(() => {
        if (props.imkey != null) getParticipantsTrigger(props.imkey);
    }, [getParticipantsTrigger, props.imkey]);

    React.useEffect(() => {
        if (allParticipants != null) {
            const newPaxList = props.isUserDetail
                ? allParticipants
                : allParticipants.filter((p: Participant) => !p.isArchived);
            setParticipants(newPaxList);
        } else {
            setParticipants([]);
        }
    }, [allParticipants, props.isUserDetail]);

    const getParticipantsWithSelectedAiKeys = (aikeys: number[]): Participant[] => {
        let pax: Participant[] = [];
        if (participants != null && participants.length > 0) {
            participants.forEach((p) => {
                const foundAssets = p.assets.filter((a) => aikeys.some((ak) => ak === a.id));
                if (foundAssets.length > 0) pax.push(p);
            });
        }
        return pax;
    };

    const getSelectedParticipantAssetsByEsiKeys = (
        esikeys: number[],
        aikeys: number[],
    ): Asset[] => {
        let assets: Asset[] = [];
        let pax: Participant[] =
            participants?.filter((p) => esikeys.some((ek) => p.esiKey === ek)) ?? [];
        if (pax != null && pax.length > 0) {
            pax.forEach((p) => {
                const foundAssets = p.assets.filter((a) => aikeys.some((ak) => ak === a.id));
                assets = assets.concat(foundAssets);
            });
        }
        return assets;
    };

    const getParticipantAssetsByAiKeys = (aikeys: number[]): Asset[] => {
        let assets: Asset[] = [];
        if (participants != null && participants.length > 0) {
            participants.forEach((p) => {
                const foundAssets = p.assets.filter((a) => aikeys.some((ak) => ak === a.id));
                assets = assets.concat(foundAssets);
            });
        }
        return assets;
    };

    const ShowSnackbarElement = (message: string, severity: AlertColor) => {
        setShowSnackbar(true);
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
    };

    const handleEvent = async (evt: string, ids: number[]) => {
        switch (evt) {
            case "download":
                if (ids === undefined || ids.length <= 0) {
                    ShowSnackbarElement("Download Not Possible", "info");
                    return;
                }

                setCurrentEvent(evt);
                let filename = "ParticipantFiles.zip";
                if (ids.length === 1) {
                    const paxAssets = getParticipantAssetsByAiKeys([ids[0]]);
                    filename = paxAssets[0].name;
                    if (paxAssets[0].fileExtension !== "") {
                        var fileExt = paxAssets[0].name.split(".").pop();
                        if (fileExt?.toLowerCase() !== paxAssets[0].fileExtension.toLowerCase())
                            filename += paxAssets[0].fileExtension;
                    }
                }

                const downloadrequest: AssetDownloadRequest = {
                    fname: filename,
                    aikeys: ids,
                    flatzip: false,
                };

                downloadService
                    .DownloadAssets(downloadrequest)
                    .then(() => {
                        logEvent("UserDownloadedParticipantFiles", null);
                        ShowSnackbarElement("Download Completed", "success");
                        setCurrentEvent("");
                    })
                    .catch((error) => {
                        ShowSnackbarElement("Download Failed", "error");
                        setCurrentEvent("");
                    });
                break;

            case "release":
                const selectedParticipants = getParticipantsWithSelectedAiKeys(ids);
                if (selectedParticipants.length === 0) {
                    ShowSnackbarElement("Release Not Possible", "info");
                    return;
                }

                const allowedEsiKeys: number[] =
                    selectedParticipants
                        .filter(
                            (p) =>
                                (p.coachCanReleasePaxFiles && !props.isUserDetail) ||
                                claimsService.IsUserCclAdminOrSuperAdmin(),
                        )
                        .map((p) => p.esiKey) ?? [];
                if (allowedEsiKeys.length <= 0) {
                    ShowSnackbarElement("Release Not Possible", "info");
                    return;
                }

                const paxAssets = getSelectedParticipantAssetsByEsiKeys(allowedEsiKeys, ids);
                // don't attempt to release consultant reports
                const releasablePaxAssets: Asset[] = paxAssets.filter(
                    (a) => a.fileType !== "Consultant Report" && !a.isReleased,
                );

                if (releasablePaxAssets === undefined || releasablePaxAssets.length <= 0) {
                    ShowSnackbarElement("Release Not Possible", "info");
                    return;
                }

                // construct the request object
                const individualsList: IndividualAssetsList[] = selectedParticipants
                    .map((p) => {
                        const thispaxassets: number[] = releasablePaxAssets
                            .filter((a) => a.imKey === p.imKey)
                            .map((pa) => pa.id);
                        return {
                            esikey: p.esiKey,
                            imkey: p.imKey,
                            aikeys: thispaxassets,
                        };
                    })
                    .filter((i) => i.aikeys.length > 0);

                if (individualsList.length <= 0) {
                    ShowSnackbarElement("Release Not Possible", "info");
                    return;
                }

                const releaserequest: IndividualAssetsReleaseRequest = {
                    individualAssetLists: individualsList.filter((i) => i.aikeys.length > 0),
                };

                setCurrentEvent(evt);

                releaseAssets(releaserequest)
                    .unwrap()
                    .then((result) => {
                        console.log(result);
                        logEvent("UserReleasedParticipantFiles", null);

                        getParticipantsTrigger(props.imkey)
                            .unwrap()
                            .then(() => {
                                ShowSnackbarElement("Release Completed", "success");
                                setCurrentEvent("");
                            })
                            .catch((error) => {
                                ShowSnackbarElement(
                                    "Release Completed. Failed Data Refresh",
                                    "success",
                                );
                                setCurrentEvent("");
                            });
                    })
                    .catch((error) => {
                        ShowSnackbarElement("Release Failed", "error");
                        setCurrentEvent("");
                    });
                break;

            case "activate":
                if (participants == null || participants.length <= 0) return;

                let allowedPax: Participant[] = participants
                    .filter((p) => ids.some((i) => i === p.esiKey))
                    .filter(
                        (p) =>
                            p.sessionHasCompassSku &&
                            p.sessionHostPlatform !== "Leadership Accelerator Platform",
                    )
                    .filter(
                        (p) =>
                            (p.coachCanActivateCompass && !props.isUserDetail) ||
                            claimsService.IsUserCclAdminOrSuperAdmin(),
                    );

                if (allowedPax.length <= 0) {
                    ShowSnackbarElement("Compass Activation Not Possible", "info");
                    return;
                }

                setCurrentEvent(evt);
                const recipientList: RecipientInfo[] = allowedPax.map((p) => {
                    return {
                        email: p.emailAddress,
                        firstName: p.firstName,
                        lastName: p.lastName,
                    };
                });

                const request: SendCompassInvitationRequest = {
                    recipients: recipientList,
                    disableEmail: false,
                    autoAccept: false,
                    ignorePendingInvites: false,
                };

                sendCompassInvitations(request)
                    .unwrap()
                    .then((response: SendInvitationResponse) => {
                        setCurrentEvent("");
                        if (response === undefined || response.numberInivitationsSent <= 0) {
                            ShowSnackbarElement("Compass Activation Failed", "error");
                            return;
                        }
                        logEvent("UserActivatedCompass", null);
                        if (response.numberFailed > 0) {
                            ShowSnackbarElement("Compass Activation Failed", "error");
                        } else {
                            if (recipientList.length < ids.length) {
                                ShowSnackbarElement("Compass Activation Failed", "error");
                            } else {
                                ShowSnackbarElement("Compass Activation Completed", "success");
                            }
                        }
                    })
                    .catch((error) => {
                        setCurrentEvent("");
                        ShowSnackbarElement("Compass Activation Failed", "error");
                    });
                break;

            case "viewstatus":
                if (ids === undefined || ids.length <= 0) return;

                props.handleViewPaxAssessmentStatus(ids[0]);
                break;

            case "archive":
            case "unarchive":
                if (ids === undefined || ids.length <= 0) return;

                if (archiveOnBehalfOfUser || evt === "archive") {
                    changeArchiveStatus(evt, ids);
                }
                break;

            default:
                return;
        }
    };

    const changeArchiveStatus = (evt: string, ids: number[]) => {
        const archiving = evt === "archive";
        const verb = archiving ? "Archive" : "Un-Archive";
        const request = archiveOnBehalfOfUser
            ? { imkey: props.imkey, esiKeys: ids, onBehalfOfImKey: props.imkey }
            : { imkey: +claimsService.GetCurrentUserId(), esiKeys: ids };

        setCurrentEvent(evt);
        (archiving ? archiveParticipants(request) : unarchiveParticipants(request))
            .unwrap()
            .then((result) => {
                setCurrentEvent("");
                if (result.length < ids.length) {
                    ShowSnackbarElement(
                        `${result.length} of ${ids.length} Participant(s) ${verb}d. The rest are not assigned to this user.`,
                        "warning",
                    );
                } else {
                    logEvent(archiving ? "ParticipantsArchived" : "ParticipantsUnarchived", null);
                    ShowSnackbarElement(`Participant(s) ${verb}d`, "success");
                }
            })
            .catch(() => {
                setCurrentEvent("");
                ShowSnackbarElement(`Participant(s) ${verb} Failed`, "error");
            });
    };

    const viewCompass = (imkey: number) => {
        // get the url
        getCompassUrl(imkey)
            .unwrap()
            .then((response: GetCompassUrlResponse) => {
                window.open(response.tempCompassUrl);
                logEvent("UserViewedCompass", null);
            })
            .catch((error) => {
                ShowSnackbarElement("View Compass Error", "error");
                setCurrentEvent("");
            });
    };

    if (isError) {
        let msg = getErrorMsg(error);
        console.log(msg);
        return (
            <div>
                <Alert severity="error" sx={{ fontSize: 14 }}>
                    <AlertTitle sx={{ fontSize: 14 }}>Error</AlertTitle>
                    {msg}
                </Alert>
            </div>
        );
    }

    if (isLoading) {
        return <ComponentLoader msg="Loading" />;
    }

    return (
        <Stack spacing={3} sx={{ width: 1 }}>
            <CclDownloadWarningAlert />
            {showSnackbar ? (
                <CclAlertSnackbar
                    open={true}
                    onClose={() => setShowSnackbar(false)}
                    message={snackbarMessage}
                    severity={snackbarSeverity}
                />
            ) : null}
            <FlexGrid>
                <ResourceRegistrationsListDataGridPro
                    isLoading={isLoading}
                    participants={participants}
                    handleEvent={handleEvent}
                    viewCompass={viewCompass}
                    allowOperations={allowParticipantOperations}
                    alternativeEventOnClickHandler={props.alternateEventOnClickHandler}
                    currentEvent={currentEvent}
                    isUserDetail={props.isUserDetail}
                    allowArchiveManagement={archiveOnBehalfOfUser}
                />
            </FlexGrid>
        </Stack>
    );
};

export default ResourceRegistrationsPanel;
